'use client';

import { useEffect } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { SitePage, PageHero, Btn, WRAP } from '@/components/site/page-kit';

const howToSchema = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  "name": "How to Buy Laminates Online in India",
  "description": "Complete guide to buying decorative laminates online covering types, finishes, pricing, and quality checks.",
  "totalTime": "PT10M",
  "step": [
    {"@type": "HowToStep", "position": 1, "name": "Determine Your Application", "text": "Identify whether you need laminates for kitchen cabinets, wardrobes, furniture, or wall panels."},
    {"@type": "HowToStep", "position": 2, "name": "Choose the Right Finish", "text": "Select from matte, glossy, textured, or acrylic finishes based on aesthetic and maintenance needs."},
    {"@type": "HowToStep", "position": 3, "name": "Browse HINCH Catalog", "text": "Use filters to explore thousands of designs by color, pattern, brand, and finish type."},
    {"@type": "HowToStep", "position": 4, "name": "Check Specifications", "text": "Review thickness, size, durability grade, and warranty information."},
    {"@type": "HowToStep", "position": 5, "name": "Request Samples", "text": "Order physical samples to verify color and texture in your lighting conditions."},
    {"@type": "HowToStep", "position": 6, "name": "Connect with Suppliers", "text": "Contact verified suppliers through HINCH for pricing and delivery details."}
  ]
};

export default function BuyingLaminatesGuidePage() {
  useEffect(() => { window.scrollTo({ top: 0, behavior: 'instant' }); }, []);

  const h2 = 'mb-6 text-[26px] leading-[1.15] tracking-[-0.02em] md:text-[32px]';

  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(howToSchema)}}/>
      <SitePage>
        <PageHero
          eyebrow="Buying Guide"
          title="How to Buy Laminates Online in India"
          description={<>
            Buying laminates online in India is now easier with platforms like HINCH that offer thousands of designs, 
            detailed specifications, price comparisons, and verified supplier connectivity. This guide covers everything 
            from selecting the right finish to ensuring quality and delivery.
          </>}
        />

        <div className="bg-white">
          <article className={`${WRAP} py-12 md:py-16`}>
            <div className="mx-auto max-w-[720px] divide-y divide-hv-line">
              <section className="pb-10 md:pb-12">
                <h2 className={h2}>Understanding Laminate Types</h2>
                <div className="space-y-3">
                  {[
                    { title: 'Decorative Laminates (1mm)', desc: 'Standard laminates for furniture, cabinets, and interior surfaces. Available in thousands of designs.' },
                    { title: 'Acrylic Laminates', desc: 'High-gloss finish with superior scratch resistance. Ideal for modern kitchens and high-traffic areas.' },
                    { title: 'Textured Laminates', desc: '3D surface patterns mimicking wood grain, stone, or fabric textures.' },
                  ].map((type) => (
                    <div key={type.title} className="rounded-xl border border-hv-line bg-hv-paper p-5">
                      <h3 className="mb-1.5 text-lg">{type.title}</h3>
                      <p className="text-[15px] leading-[1.8] text-hv-soft">{type.desc}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section className="py-10 md:py-12">
                <h2 className={h2}>Step-by-Step Buying Process</h2>
                <ol className="space-y-6">
                  {howToSchema.step.map((step, idx) => (
                    <li key={idx} className="flex gap-4">
                      <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-hv-ink font-hv-mono text-[13px] text-hv-yellow">
                        {step.position}
                      </div>
                      <div className="pt-1">
                        <h3 className="mb-1.5 text-lg">{step.name}</h3>
                        <p className="text-[15px] leading-[1.8] text-hv-soft">{step.text}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </section>

              <section className="pt-10 md:pt-12">
                <h2 className={h2}>Quality Checks</h2>
                <ul className="space-y-2.5 text-[15px] leading-[1.8] text-hv-soft">
                  {['Verify brand authenticity and warranty', 'Check thickness (0.8mm to 1mm for standard)', 'Inspect finish quality and consistency', 'Confirm edge banding compatibility', 'Review supplier ratings and reviews'].map((item, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <CheckCircle2 className="mt-[5px] h-[18px] w-[18px] flex-shrink-0 text-hv-orange" strokeWidth={1.8}/>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </article>
        </div>

        <section className="bg-hv-paper">
          <div className={`${WRAP} py-12 md:py-16`}>
            <div className="mx-auto flex max-w-[720px] flex-col items-start gap-5 rounded-[20px] bg-hv-yellow p-6 md:p-10">
              <h2 className="text-[26px] leading-[1.1] tracking-[-0.02em] md:text-[32px]">Start Exploring on HINCH</h2>
              <p className="text-hv-ink/80">Browse 10,000+ laminate designs with detailed specifications and instant supplier connectivity.</p>
              <Btn href="/" kind="dark" arrow>Browse Laminates</Btn>
            </div>
          </div>
        </section>
      </SitePage>
    </>
  );
}
